import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Session } from '@supabase/supabase-js'
import { supabase } from '../supabaseClient'
import { User, UserRole, LifecycleState } from '../types'

interface StudentLifecycle {
  student_id:       string
  lifecycle_state:  LifecycleState
  trial_started_at: string | null
}

interface AuthContextValue {
  session:           Session | null
  profile:           User    | null
  role:              UserRole | null
  studentLifecycle:  StudentLifecycle | null   // null when role !== 'student' or profile missing
  trialDaysLeft:     number | null              // null when not in trial_active
  mustChangePassword: boolean                  // true when user still holds the staff-set default
  loading:           boolean
  signOut:           () => Promise<void>
  refreshLifecycle:  () => Promise<void>
  clearMustChangePassword: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  profile: null,
  role:    null,
  studentLifecycle: null,
  trialDaysLeft:    null,
  mustChangePassword: false,
  loading: true,
  signOut: async () => {},
  refreshLifecycle: async () => {},
  clearMustChangePassword: async () => {},
})

const TRIAL_DURATION_DAYS = 60

function computeDaysLeft(trialStartedAt: string | null, state: LifecycleState | null): number | null {
  if (state !== 'trial_active' || !trialStartedAt) return null
  const elapsedMs = Date.now() - new Date(trialStartedAt).getTime()
  const elapsedDays = Math.floor(elapsedMs / 86400000)
  return Math.max(0, TRIAL_DURATION_DAYS - elapsedDays)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<User | null>(null)
  const [studentLifecycle, setStudentLifecycle] = useState<StudentLifecycle | null>(null)
  const [loading, setLoading] = useState(true)
  // Tracks the signed-in user id so we can ignore auth events that keep the same
  // user (token refresh, tab re-focus). Without this, Supabase hands us a NEW
  // session object on every refresh; setSession(newObj) then re-runs every
  // `[session]`-dependent effect across the app — refetching data and wiping
  // unsaved in-progress work (e.g. a running AI review on the mentor review page).
  const currentUserIdRef = useRef<string | null>(null)

  async function fetchStudentLifecycle(userId: string) {
    try {
      const { data, error } = await supabase
        .from('student_profile')
        .select('student_id, lifecycle_state, trial_started_at')
        .eq('user_id', userId)
        .maybeSingle()
      if (error) {
        console.error('[Auth] fetchStudentLifecycle error:', error.message)
        return
      }
      setStudentLifecycle(data ?? null)
    } catch (e) {
      console.error('[Auth] fetchStudentLifecycle threw:', e)
    }
  }

  async function fetchProfile(userId: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) {
        console.error('[Auth] fetchProfile error:', error.message)
        return
      }
      if (!data) {
        console.warn('[Auth] No users row for', userId)
        return
      }

      if (data.status === 'suspended' || data.status === 'inactive') {
        await supabase.auth.signOut()
        return
      }

      setProfile(data)

      // Pull lifecycle for student users — gates feature access
      if (data.role === 'student') {
        await fetchStudentLifecycle(userId)
      } else {
        setStudentLifecycle(null)
      }

      const updates: Record<string, string> = { last_login: new Date().toISOString() }
      if (data.status === 'pending_activation') updates.status = 'active'
      const { error: updateErr } = await supabase
        .from('users')
        .update(updates)
        .eq('user_id', userId)
      if (updateErr) console.warn('[Auth] last_login update failed:', updateErr.message)
    } catch (e) {
      console.error('[Auth] fetchProfile threw:', e)
    }
  }

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        currentUserIdRef.current = session?.user.id ?? null
        setSession(session)
        setLoading(false)
        if (session) fetchProfile(session.user.id)
      })
      .catch(e => {
        console.error('[Auth] getSession threw:', e)
        setLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user.id ?? null
      // Same user (token refresh / tab re-focus) → no identity change. Skip the
      // state update so we don't churn a new session reference and re-run every
      // `[session]` effect (which would refetch and discard unsaved work).
      if (newUserId === currentUserIdRef.current) return
      currentUserIdRef.current = newUserId
      setSession(session)
      if (session) fetchProfile(session.user.id)
      else { setProfile(null); setStudentLifecycle(null) }
    })

    function onVisible() {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().catch(e => console.error('[Auth] visibility refresh failed:', e))
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function signOut() {
    await supabase.auth.signOut()
    setProfile(null)
    setStudentLifecycle(null)
  }

  async function refreshLifecycle() {
    if (session?.user.id) await fetchStudentLifecycle(session.user.id)
  }

  async function clearMustChangePassword() {
    if (!session?.user.id) return
    const { error } = await supabase
      .from('users')
      .update({ must_change_password: false })
      .eq('user_id', session.user.id)
    if (error) {
      console.error('[Auth] clearMustChangePassword error:', error.message)
      return
    }
    setProfile(prev => prev ? { ...prev, must_change_password: false } : prev)
  }

  const trialDaysLeft = computeDaysLeft(
    studentLifecycle?.trial_started_at ?? null,
    studentLifecycle?.lifecycle_state ?? null,
  )

  return (
    <AuthContext.Provider value={{
      session,
      profile,
      role: profile?.role ?? null,
      studentLifecycle,
      trialDaysLeft,
      mustChangePassword: profile?.must_change_password ?? false,
      loading,
      signOut,
      refreshLifecycle,
      clearMustChangePassword,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
